import { useMemo } from 'react';

import { useLeagueUpcomingEventsQuery } from '@/api/events/query.generated';
import { useLiveEventsPropsUpdate } from '@/feature/betslip-pickem/hooks/use-live-props-update';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { generateLobbyPlayerCards } from '@/feature/lobby/utils/lobbyPlayers';
import { League, PlayerAttributesType } from '@/types/api.generated';
import { findPlayerAttribute } from '@/utils/fantasy-attribute-utils';
import fuzzysort from 'fuzzysort';

export const usePlayersSearch = (query: string, league: League) => {
    const [{ data: eventsWithPlayers, fetching: loading }] = useLeagueUpcomingEventsQuery({
        variables: { league: league },
        requestPolicy: 'cache-and-network',
        pause: !league && query.length <= 2,
    });

    useLiveEventsPropsUpdate({
        events: eventsWithPlayers?.getUpcomingEventsV2,
        options: { subscribeToGameClockUpdates: true, subscribeToMarketUpdates: true, debugTag: 'usePlayersSearch' },
    });

    const players: PlayerWithTeamAndEvent[] = useMemo(() => {
        return generateLobbyPlayerCards(eventsWithPlayers?.getUpcomingEventsV2 || []);
    }, [eventsWithPlayers?.getUpcomingEventsV2]);

    const playersToDisplay = useMemo(() => {
        const sortedResults = fuzzysort.go(query, players, {
            keys: [
                // Custom rules to select by which "key" to search
                playerWithEvent => playerWithEvent.lastName,
                playerWithEvent => playerWithEvent.firstName,
                // search by player's full name even if the user misspells/shortens the name
                // eg for "R Wilson" it will match "Russell Wilson"
                playerWithEvent => `${playerWithEvent.firstName} ${playerWithEvent.lastName}`,
                playerWithEvent => playerWithEvent.position,
                playerWithEvent => playerWithEvent.team?.name || '',
                playerWithEvent => playerWithEvent.team?.fullName || '',
                // if is PGA we search by country abbreviation (USA, KOR)
                playerWithEvent => findPlayerAttribute(playerWithEvent, PlayerAttributesType.CountryCode) || '',
                playerWithEvent => findPlayerAttribute(playerWithEvent, PlayerAttributesType.CountryName) || '',
            ],
        });

        return sortedResults.map(({ obj }) => obj);
    }, [players, query]);

    return { playersToDisplay, loading };
};
